/**
 * This component will find a RewardsId with phoneNumber or email
 *
 * @module views/components/UltamateRewardsIdLookUpLocate
 * @memberof -Common
 */
import './UltamateRewardsIdLookUpLocate.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import Image from '@ulta/core/components/Image/Image';
import InputField from '@ulta/core/components/InputField/InputField';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { constants } from '@ulta/utils/constants/constants';

import BlockMessage from '../BlockMessage/BlockMessage';
import Divider from '../Divider/Divider';
import * as utils from './UltamateRewardsIdLookUpLocate';

/**
 * Represents a UltamateRewardsIdLookUpLocate component
 *
 * @method
 * @param { UltamateRewardsIdLookUpLocateProps} props - React properties passed from composition
 * @returns UltamateRewardsIdLookUpLocate
 */
export const UltamateRewardsIdLookUpLocate = React.forwardRef( ( props, _ )=>{
  const {
    buttonAction,
    componentKey,
    conditionMessageLabel,
    containerLayerHostContentId,
    email,
    helperText,
    helpImage,
    invokeMutation,
    otherWaysToFindLabel,
    phoneNumber,
    title,
    subTitle,
    messages,
    showError
  } = props;

  const formikRef = useRef();
  const phoneNumberRegex = new RegExp( constants.REGEX_PATTERNS.dashedPhoneNumberRegex );
  const emailRegex = new RegExp( constants.REGEX_PATTERNS.emailRegex );

  const initialValues = {
    email: email?.value || '',
    phoneNumber: phoneNumber?.value || ''
  };

  const validationSchema = Yup.object( {
    phoneNumber: Yup.string().matches( phoneNumberRegex, 'Please enter a valid phone number' ),
    email: Yup.string().matches( emailRegex, 'Please enter a valid email address' )
  } );
  const wrapperRef = useRef();

  // when findMYId section is rendered the focus should be on the first input field
  useEffect( () => {
    const firstInputField = phoneNumber?.name ? 'input[name=phoneNumber]' : 'input[name=email]';
    wrapperRef.current?.querySelector( firstInputField )?.focus();
  }, [] );

  const onSubmit = useCallback(
    utils.handleSubmit(
      { contentId: containerLayerHostContentId, action: buttonAction },
      { invokeMutation }
    ),
    [containerLayerHostContentId, buttonAction, invokeMutation]
  );

  return (
    <div className='UltamateRewardsIdLookUpLocate'
      ref={ wrapperRef }
    >
      <div className='UltamateRewardsIdLookUpLocate__title'>
        <Text htmlTag='h2'
          textStyle='body-1'
        >
          { title }
        </Text>
      </div>
      <div className='UltamateRewardsIdLookUpLocate__subtitle'>
        <Text htmlTag='p'
          textStyle='body-2'
          color='neutral-600'
        >
          { subTitle }
        </Text>
      </div>
      { showError &&
        messages?.map( ( data, index ) => (
          <div className='UltamateRewardsIdLookUpLocate__blockErrorMessage'
            key={ `${index}-${componentKey}` }
          >
            <BlockMessage
              message={ data.message }
              messageType={ data.icon }
              backgroundColor={ data.backgroundColor }
              borderColor={ data.borderColor }
              textColor={ data.textColor }
              dismissAction={ data.dismissAction }
              action1={ data.action1 }
              action1Style='link'
              action2={ data.action2 }
              ariaLiveType={ 'assertive' }
              role={ 'status' }
            />
          </div>
        ) ) }
      <Formik innerRef={ formikRef }
        initialValues={ initialValues }
        validationSchema={ validationSchema }
        onSubmit={ onSubmit }
      >
        { () => (
          <>
            <Form>
              { phoneNumber && (
                <div className='UltamateRewardsIdLookUpLocate__field'>
                  <InputField
                    { ...spreadProps( { ...phoneNumber } ) }
                    formatter={ constants.FORMATTER_PATTERNS.DASHED_PHONE_NUMBER }
                    autoComplete='phoneNumber'
                  />
                </div>
              ) }
              { phoneNumber && email && (
                <div className='UltamateRewardsIdLookUpLocate__orDivider'>
                  <div className='UltamateRewardsIdLookUpLocate__lableOr'>
                    <Text htmlTag='h3'
                      textStyle='body-2'
                    >
                      { conditionMessageLabel }
                    </Text>
                  </div>
                  <Divider type={ 'gray' }> </Divider>
                </div>
              ) }
              { email && (
                <div className='UltamateRewardsIdLookUpLocate__field'>
                  <InputField
                    { ...spreadProps( { ...email } ) }
                    autoComplete='email'
                  />
                </div>
              ) }
              { buttonAction && (
                <div className='UltamateRewardsIdLookUpLocate__ButtonAction'>
                  <Button
                    id='buttonSubmit'
                    type='submit'
                    secondary
                    label={ buttonAction.label }
                    fullWidth
                    withHover
                  />
                </div>
              ) }
            </Form>
            { helperText && phoneNumber && (
              <div className='UltamateRewardsIdLookUpLocate__helperText'>
                <Text htmlTag='p'
                  textStyle='body-3'
                  color='neutral-600'
                >
                  { helperText }
                </Text>
              </div>
            ) }
            { otherWaysToFindLabel && (
              <div className='UltamateRewardsIdLookUpLocate__findLabel'>
                <Text htmlTag='h2'
                  textStyle='title-6'
                >
                  { otherWaysToFindLabel }
                </Text>
              </div>
            ) }
            { hasItems( helpImage ) && (
              helpImage.map( ( image, index ) => (
                <div className='UltamateRewardsIdLookUpLocate__ImageWrapper'
                  key={ `${componentKey}-need-help-image-${index}` }
                >
                  <Image
                    src={ image.imageUrl }
                    alt={ image.altText }
                  />
                  <Text htmlTag='p'
                    textStyle='body-2'
                    color='neutral-600'
                  >
                    { image.altText }
                  </Text>
                </div>
              ) )
            ) }
          </>
        ) }
      </Formik>
    </div>
  );
} );

/**
 * Handle submit
 * @param {object} data - Arguments
 * @param {object} data.props - Component props
 * @param {object} data.vaues - Form values
 * @param {object} methods - Methods
 */
export const handleSubmit = ( data, methods ) => ( values = {} ) => {
  const { action, contentId } = data || {};
  const { invokeMutation } = methods || {};
  const { graphql, customHeaders, params } = action || {};

  if(
    !graphql || !invokeMutation
  ){
    return;
  }

  const { origin, pathname } = global.location;

  let keys = Object.keys( values );
  let inputs = [];

  keys.forEach( ( key ) => {
    inputs.push( { [key]: { name: key, value: values[key].trim() } } );
  } );

  const moduleParams = Object.assign( {}, ...inputs, {
    originalUrl:`${origin}${pathname}`
  } );

  invokeMutation( {
    graphql,
    customHeaders,
    params,
    variables: {
      moduleParams,
      contentId
    }
  } );
};

/**
 * Property type definitions
 * @typedef UltamateRewardsIdLookUpLocateProps
 * @type {object}
 * @property {object} buttonAction - sets the buttonAction properties
 * @property {string} componentKey - sets componentKey
 * @property {string} conditionMessageLabel - Sets the conditionMessageLabel
 * @property {string} subTitle - sets subTitle
 * @property {string} title - sets title
 * @property {string} containerLayerHostContentId - sets containerLayerHostContentId
 * @property {object} phoneNumber - Sets the phoneNumber properties
 * @property {object} email - Sets the email properties
 * @property {string} helperText - Sets the helperText
 * @property {array} helpImage - array of images for findMyIdSection
 * @property {string} otherWaysToFindLabel - label property for otherWaysToFind rewards
 */
export const propTypes =  {
  buttonAction: PropTypes.object,
  componentKey: PropTypes.string,
  conditionMessageLabel: PropTypes.string,
  containerLayerHostContentId: PropTypes.string,
  email: PropTypes.object,
  helperText: PropTypes.string,
  helpImage: PropTypes.array,
  otherWaysToFindLabel: PropTypes.string,
  phoneNumber: PropTypes.object,
  subTitle: PropTypes.string,
  title: PropTypes.string
};

UltamateRewardsIdLookUpLocate.propTypes = propTypes;
UltamateRewardsIdLookUpLocate.displayName = 'UltamateRewardsIdLookUpLocate';

export default UltamateRewardsIdLookUpLocate;
